import { FC, useCallback, useState } from 'react'
import { Avatar, Box, MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material'
import { SnsConnectionInfoFragment, useViewerQuery } from '~/generated/graphql'
import { ConnectInstagramAccountDialog } from './Facebook/ConnectInstagramAccount/ConnectInstagramAccountDialog'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import InstagramIcon from '@mui/icons-material/Instagram'
import { pagesPath } from '~/lib/$path'
import NextLink from 'next/link'
import { anchorIdInstaConnections } from '~/constants/anchorIds'

const ADD_NEW_ACCOUNT = 'ADD_NEW_ACCOUNT'

const useConnectDialog = () => {
  const [open, setOpen] = useState(false)
  const handleOpen = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => setOpen(false), [])
  return {
    dialog: <ConnectInstagramAccountDialog isOpen={open} onClose={handleClose} />,
    open: handleOpen,
  }
}

export const useSelectInstagramAccount = (onAccountChanged?: (id: string) => void, defaultId?: string) => {
  const { open: onOpenConnectDialog, dialog: connectDialog } = useConnectDialog()
  const [selectedAccountId, setSelectedAccountId] = useState(defaultId ?? '')
  const { data, loading } = useViewerQuery({
    onCompleted: (data) => {
      if (selectedAccountId) return // アカウント再連携後にも呼ばれてしまうため

      const defaultSelectedAccountId = data.viewer.company?.snsConnections?.[0]?.id ?? ''
      setSelectedAccountId(defaultSelectedAccountId)
      if (defaultSelectedAccountId) onAccountChanged?.(defaultSelectedAccountId)
    },
  })
  const connections = data?.viewer.company?.snsConnections ?? []

  const handleChangeAccount = (e: SelectChangeEvent<string>) => {
    if (e.target.value === ADD_NEW_ACCOUNT) return onOpenConnectDialog()

    setSelectedAccountId(e.target.value || '')
    onAccountChanged?.(e.target.value || '')
  }

  const selectAccount = (
    <>
      <SelectInstagramAccount
        selectedAccountId={selectedAccountId}
        onChange={handleChangeAccount}
        loading={loading}
        connections={connections.filter(
          (x): x is SnsConnectionInfoFragment => x?.__typename === 'CompanySnsConnection',
        )}
      />
      {connectDialog}
    </>
  )

  return {
    selectAccount,
    selectedInstagramAccount: connections.find((x) => x?.id === selectedAccountId),
    snsConnections: connections,
    isViewerLoading: loading,
  }
}

type Props = {
  selectedAccountId: string
  onChange: (e: SelectChangeEvent) => void
  loading?: boolean
  connections: SnsConnectionInfoFragment[]
}

const SelectInstagramAccount: FC<Props> = ({ selectedAccountId, onChange, loading, connections }) => {
  return (
    <Box>
      <Select
        size="small"
        variant="standard"
        onChange={onChange}
        value={selectedAccountId}
        disabled={loading || connections.length === 0}
        sx={{ width: '250px' }}
        renderValue={(value) => {
          const currentAccount = connections.find((x) => x.id === value)
          if (!currentAccount) return null
          return (
            <Box sx={{ pt: 0.3, pl: 1 }}>
              <Item name={currentAccount.name} imageUrl={currentAccount.imageUrl ?? ''} />
            </Box>
          )
        }}
      >
        {connections.map((item) => {
          if (!item.id) return null
          return (
            <MenuItem key={item.id} value={item.id}>
              <Item name={item.name} imageUrl={item.imageUrl ?? ''} />
            </MenuItem>
          )
        })}

        <MenuItem value={ADD_NEW_ACCOUNT} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon sx={{ color: 'gray' }} fontSize="small" />
          アカウントを追加する
        </MenuItem>
        <MenuItem sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NextLink
            href={pagesPath.adv_account.personal_info.$url().pathname + `#${anchorIdInstaConnections}`}
            passHref
            legacyBehavior
          >
            <Stack
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                gap: 1,
              }}
            >
              <RemoveIcon sx={{ color: 'gray' }} fontSize="small" />
              アカウント連携を解除する
            </Stack>
          </NextLink>
        </MenuItem>
      </Select>
    </Box>
  )
}

const Item: FC<{ name: string; imageUrl: string }> = ({ name, imageUrl }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ position: 'relative' }}>
        <Avatar src={imageUrl} sx={{ width: '25px', height: '25px' }} />
        <InstagramIcon
          sx={{
            fontSize: '10px',
            position: 'absolute',
            bottom: '0%',
            right: '0%',
            color: '#EA3EF7',
          }}
        />
      </Box>
      <Typography variant="body2">{name}</Typography>
    </Box>
  )
}
